// Header "Save and Submit": validates the visible fields, creates the assembly
// item in NetSuite (which triggers the routing + BOM scripts), assigns any
// Customer Part Number, and writes the configured line back to the sales order
// that launched cpq2 — then returns to that order. No preview/confirmation step.
// This is a REAL production write.

import { useState, useRef, useEffect } from "react";
import { useAnswers } from "../state/AnswersContext";
import { buildAssembly } from "../util/assemblyBuilder";
import { deriveNestedChild, substituteNestedChild } from "../util/nestedChild";
import { createTimer } from "../util/timing";
import { loadRoutings, type CpqData } from "../data/loader";
import {
  createAssembly,
  createCustomerPartNumber,
  fireBackground,
  finishAssemblyInBackground,
  findItemByFingerprint,
  setItemBasePrice,
  refreshItemText,
  isAmbiguousCreateFailure,
} from "../util/netsuiteClient";
import { findBlockingUnanswered, findUnansweredVisible, visibleQuestionCodes } from "../util/validation";
import { findUnfinishedFields } from "../util/unfinishedConfig";
import { diffConfigs } from "../util/configDiff";
import { reportConfigChange } from "../util/configChange";
import { findOverLimitSizes } from "../util/sizeLimits";
import { stripHtml } from "../util/text";
import { recordError, describeThrown, type ErrorReport } from "../util/errorLog";
import { reportMissingParts } from "../util/missingPartNotice";
import { findInvalidHousingHeight, housingHeightKeys } from "../util/housingHeight";
import { getLaunchContext } from "../util/launchContext";
import { describeSaveOutcome } from "../util/saveOutcome";
import {
  buildCustomerRequirements,
  CUSTOMER_REQUIREMENTS_REFERENCE_FIELDS,
} from "../util/customerRequirements";
import { buildInletConnectionNotes } from "../util/inletNotes";
import {
  openedFromSalesOrder,
  postConfiguredLine,
  CONFIGURED_LINE_AMOUNT,
} from "../util/salesOrderBridge";
import { buildSerialTagLine, resolveSerialTagItem } from "../util/serialTag";
import { priorLineDescriptions } from "../util/priorLines";
import { buildAdditionalLines, prefetchAdditionalLines, resolveAdditionalLines } from "../util/additionalLines";
import { buildOrderFields } from "../util/orderFields";
import { lookupNdbPrice, describeNdbPriceKey } from "../util/ndbPricing";
import { awardConfigurationPoints } from "../util/pointsClient";
import { getProfile } from "../products";
import { isQuoteLaunch } from "../util/quoteScope";
import { attachCustomerFiles } from "../util/customerFiles";
import { noteBlockedSubmit, noteSubmitted } from "../util/sessionTrack";
import { fetchOppositeSide, logPoFeedback, logPredictionOutcome, type PredictionResponse } from "../util/predictionClient";
import { currentFieldwise, currentPoLine, currentPoUse } from "../util/fieldwiseRun";
import { flatten } from "../util/fieldwise";
import { stashOppositeSide } from "../util/pendingMirror";
import { scoredKeys } from "../util/prediction";

type Props = {
  data: CpqData;
  // Called with the first field that needs an answer so the parent can switch to
  // its tab and scroll it into view (see Configurator.focusInvalidField).
  onFocusInvalid?: (tabId: string, code: string) => void;
};
type Phase = "idle" | "submitting" | "done" | "error";

// WHICH SUBMIT STEPS RUN is declared by the configurator, not decided here — see
// src/products/. Every step below is gated on `steps.<name>`, so a product gets a
// step only by asking for it.
//
// This is not tidiness. Three of these steps do NOT fail safe when handed a config
// they were not written for:
//   serialTag            resolveSerialTagItem never fails; it falls back to
//                        "Serial Tag - Flameco" so a lookup failure can never block a
//                        Natural Draft submit. Ungated, it yields the WRONG tag, not
//                        no tag — and the write-back never removes a line.
//   customerRequirements writes a real customrecord_customer_requirements record
//                        against a live customer, built from Natural Draft answers.
//   ndbItemFields        the value maps are keyed by Natural Draft ANSWER CODES, so
//                        for another product every lookup misses (Core Type is the
//                        clearest: NA / TUBE / PIPE here vs CORE_PIPE /
//                        CORE_ROUND_ROD_MACHINED in Flame Cells).
// The other two (additionalLines, listPricing) do happen to resolve to nothing when
// their answer keys are absent, but resolving-to-nothing-by-accident is exactly the
// property that stops holding the moment two products share a code.

// The built Sales Description (the "DESCRIPTION" op's output) reaches the item
// through the ITCR field mapping, which maps the REST property `description` to
// {DESCRIPTION/DESCRIPTION} — the same value read into salesDesc below.
//
// It is NOT set from here. This used to add itemFields.salesdescription, which
// did nothing: `salesdescription` is not a REST property on assemblyItem /
// serializedAssemblyItem (see npm run check:itemfields), so NetSuite dropped it
// and returned 200 — the same silent failure that hid basePrice. The value
// landed anyway via the ITCR key, so nothing was lost, but the line implied a
// write that was not happening. Renaming it to `description` would double-write
// the identical value; the ITCR mapping is the one place that owns it.

// Core Type (question CORE_TYPE) → custitemflamecell_coretype internal id. cpq2
// writes this directly on the item (it is NOT in the encoded mapping), so the
// NetSuite "1103 ndb mapping" decoder never touches it. Answer code → the
// field's list-value id.
const CORE_TYPE_IDS: Record<string, string> = {
  NA: "9",
  STD_LARGE_CORE_PIPE: "1",
  LN_SMALL_CORE_PIPE: "2",
  LN_LARGE_CORE_PIPE: "3",
  STD_SMALL_CORE_PIPE: "4",
  TUBE: "6",
  PIPE: "7",
  ROUND_ROD_MACHINED: "8",
};

// Hood Sight Tube (question HOOD_SIGHT_TUBE) → custitem_hoodsighttube list id.
// None = 1, 1-5/8" = 2. Written directly on the item like Core Type above (not
// in the encoded mapping); round-trips via the config snapshot on Edit Config.
const HOOD_SIGHT_TUBE_IDS: Record<string, string> = {
  NONE: "1",
  TUBE_1_5_8: "2",
};

// Burner Below Centerline (question BURNER_BELOW_CENTERLINE) → custitem_burnerbelowcenter
// list id. Written directly on the item like Core Type / Hood Sight Tube above
// (not in the encoded mapping); round-trips via the config snapshot on Edit Config.
const BURNER_BELOW_CENTERLINE_IDS: Record<string, string> = {
  BBC_0: "1", // 0"
  BBC_1: "2", // 1"
  BBC_1_1_2: "3", // 1-1/2"
  BBC_2: "4", // 2"
};

// THE ITEM'S LIST PRICE IS WRITTEN IN PHASE 1, before the user is handed back to the order.
//
// Kevin, 2026-08-27: "leave price in phase 1 but we can make it lighter". Re-decided
// 2026-10-16, after phase 2 was built and measured (~1.9s off a new item's wait, ~3s off an
// edit's): "price needs to be in phase 1 because if the sales rep goes back to the sales
// order and notices that the price is missing then they will see its a problem." The order
// LINE does not need it -- buttons.js writes it at price level Custom with cpq2's rate -- but
// the person looking at the order does, and a part that looks unpriced is a support call.
//
// So this stays false. Lighter instead: an existing item's price is checked with one SuiteQL
// read before the ~1.2s full read (checkFirst in api/shared/item-price.js), and most edits
// stop there. The phase-2 path (deferPrice / finish-assembly) is kept and tested
// (test:deferprice); it also prices an item recovered from a timed-out create when nothing
// else did.
const DEFER_ITEM_PRICE = false;

export default function SaveSubmitButton({ data, onFocusInvalid }: Props) {
  const {
    // tables/selectedRows are read only to derive a two-level BOM's child (nestedChild.ts),
    // which re-runs the pipeline over this same configuration minus its face plate.
    answers, tables, selectedRows,
    setInvalidFields, customerPartNumber, getConfigSnapshot, extraAuxColumns,
    editItemId, configMode, mappingSpec, adminOverride, editExistingItem,
    getEditBaseline, setEditBaseline,
    // The badge's lookup, and which fingerprint it answers. Reused at submit when it
    // matches -- see cacheUsable below.
    existingItem: cachedExistingItem, existingItemFor, plannedName,
    prediction, predictionOutcome, mirrorRequiredCells,
  } = useAnswers();
  // Which submit steps this configurator asks for. See the note above the imports:
  // several of them do NOT degrade safely when handed a config they were not written
  // for, so nothing below runs unless the running product declares it.
  const steps = getProfile(data.slug).submit;
  const fingerprintKey = getProfile(data.slug).fingerprintAnswerKey;
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string>("");
  const [returning, setReturning] = useState(false);
  // The opposite side of the MB housing just added to the order, offered instead of the
  // automatic return (see offerOppositeSide).
  const [opposite, setOpposite] = useState<PredictionResponse | null>(null);

  // Extra context for whatever error dialog comes next. Set it just BEFORE failing, at the
  // sites that know something worth keeping (the step, the HTTP status, the timing). It is a
  // ref rather than state because it must be readable in the same tick it is written, and it
  // must never cause a re-render on a path that is already going wrong.
  const errorContextRef = useRef<Partial<ErrorReport>>({});
  // Which message has already been recorded, so a re-render cannot file it twice.
  const recordedRef = useRef<string>("");

  // HOW DID THE PREDICTION DO. Kevin, 2026-10-17: "if the prediction is 100% correct then all
  // they have to do is look over the answers change nothing then save and submit and it will be
  // considered a success." Recorded once, when a submit FINISHES -- every success path ends in
  // phase "done", including Existing Config, which is exactly where a fully right prediction of
  // a repeat order lands. Compares the visible answers with what the prediction put there.
  // ...except that it did not: the ordinary success, a line handed back to the sales order,
  // stays in "submitting" with `returning` set and closes the tab, so nothing was recorded
  // (0 rows to 2026-10-17). `returning` counts as finished too; the call is keepalive, so the
  // tab closing 800 ms later does not cut it off.
  const predictionLoggedRef = useRef(false);
  useEffect(() => {
    if ((phase !== "done" && !returning) || predictionLoggedRef.current || !prediction) return;
    predictionLoggedRef.current = true;
    // Only what the rep could see: a vote on a question a hide rule keeps off the form (an
    // FAH's burner sections, a Box's hinge) is not a prediction the rep could have taken.
    const visible = visibleQuestionCodes(data, answers);
    const scoredNow = scoredKeys(data.questions, data.tabs);
    const out = predictionOutcome((k) => scoredNow(k) && visible.has(k.indexOf("/") < 0 ? k : k.slice(0, k.indexOf("/"))));
    if (!out) return;
    const ctx = getLaunchContext();
    logPredictionOutcome({
      product: data.slug,
      customerId: ctx.customerId,
      userId: ctx.userId,
      userName: ctx.userName,
      originType: ctx.originType,
      originTranid: ctx.transactionId ?? ctx.custbody102,
      itemName: String(answers["HOUSING_VALIDATION/HOUSING_VALIDATION"] ?? "").trim() || null,
      launchSourceId: prediction.launchSource.id,
      refineSourceId: prediction.refineSource?.id ?? null,
      predictionOn: prediction.on,
      predictedAnswers: out.predicted,
      changedAnswers: out.changed.length,
      changedKeys: out.changed,
      changedDetail: out.detail,
      predictedBySource: out.bySource,
    });
    // THE CUSTOMER'S PO (PO-PREDICTION.md §8): what it said vs what was submitted, per answer,
    // so the customer's PO profile learns where their POs are right and where they are not.
    const use = currentPoUse(), poFlat = currentPoLine(), fw = currentFieldwise();
    if (use && poFlat && fw) {
      const final = flatten(answers, fw.optionOrder);
      logPoFeedback({
        customerId: ctx.customerId, transactionId: ctx.transactionId, fileId: use.fileId, poNumber: use.number, lineIndex: use.line,
        itemName: String(answers["HOUSING_VALIDATION/HOUSING_VALIDATION"] ?? "").trim() || null, userName: ctx.userName,
        pairs: Object.entries(poFlat).filter(([k]) => k !== "ORDER_TYPE").map(([k, po]) => ({ key: k, po, final: final[k] ?? null })),
      });
    }
    // Once per finished submit; the values are read as they stand at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, returning]);

  // ONE place that records every error dialog. Hooking the PHASE rather than each of the
  // seven `setPhase("error")` sites means a new failure path is captured the day it is
  // written, instead of the day someone remembers to instrument it -- and it cannot drift out
  // of step with what the user was actually shown, because it reads the same message.
  useEffect(() => {
    if (phase !== "error") return;
    const shown = String(message || "").trim();
    if (!shown || recordedRef.current === shown) return;
    recordedRef.current = shown;
    recordError({
      message: shown,
      product: data.slug,
      configMode,
      ...errorContextRef.current,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, message]);
  // Inline (non-modal) validation error: which visible fields still need an
  // answer. Kept out of any dialog so the red-highlighted fields stay visible.
  const [missing, setMissing] = useState<string[]>([]);
  // Once the unanswered-fields warning has shown, a second Save click overrides
  // it and submits anyway (the user chose to save an incomplete configuration).
  const [bypassArmed, setBypassArmed] = useState(false);
  // HARD block (NOT bypassable): an invalid Housing Height. Unlike an unanswered
  // field, an unmapped height would silently encode to a blank on the item, so
  // it must be fixed before submitting. Holds the offending value, or "".
  const [heightBlock, setHeightBlock] = useState<string>("");
  // HARD block (NOT bypassable): a typed size over its product's ceiling -- Cell OD over
  // 99.99", which would give the part number a 6-digit OD. Holds the message, or "".
  const [sizeBlock, setSizeBlock] = useState<string>("");
  // HARD block (NOT bypassable): required fields left blank whose absence would
  // change the generated part number — "Tab › Label" strings, empty when clear.
  const [blockedFields, setBlockedFields] = useState<string[]>([]);
  // True while the Existing Config path runs — the config already matches a
  // NetSuite item, so we build NOTHING. Drives the progress modal's wording so it
  // never claims to be creating/building/rebuilding anything.
  const [existingFlow, setExistingFlow] = useState(false);
  // True once we've decided to actually build (past the existing-item check). Until
  // then the modal shows neutral "Submitting…" text, so the create/build wording
  // never flashes before we know whether the config already exists.
  const [buildStarted, setBuildStarted] = useState(false);

  const partNumber = customerPartNumber.trim();
  const customerId = getLaunchContext().customerId;
  const customerName = getLaunchContext().customerName;
  // Edit-vs-New config mode comes from AnswersContext (see there). In 'edit'
  // mode `editItemId` is the launched item we UPDATE in place (createAssembly
  // editItemId); in 'new' mode it's null, so we create a new part. It starts
  // from the Edit Config launch but flips to 'new' when the item is locked
  // (custitem_cannoteditcpq) or the user loads a different configuration.
  const isEdit = configMode === "edit";
  const fromSO = openedFromSalesOrder();

  // WARM THE LINE LOOKUPS WHILE THE USER CONFIGURES.
  //
  // The Serial Tag depends on the customer alone and the additional lines on part numbers the
  // configuration fixes long before submit, yet both were first asked at the click -- ~1.4s
  // and ~1.1s, the whole of an Existing Config save's wait once its item upkeep moved off it.
  // Both caches keep only successful answers, so a blip here is simply asked again at submit.
  const serialTagStep = steps.serialTag;
  const additionalLinesStep = steps.additionalLines;
  useEffect(() => {
    if (fromSO && serialTagStep) void resolveSerialTagItem(customerId, customerName);
  }, [fromSO, serialTagStep, customerId, customerName]);
  useEffect(() => {
    if (!fromSO || !additionalLinesStep) return;
    // Once the answers settle: the part numbers only move when the configuration does.
    const t = window.setTimeout(() => {
      try {
        prefetchAdditionalLines(buildAdditionalLines(answers, data.questions));
      } catch {
        /* a warm-up only; the submit resolves for real */
      }
    }, 1500);
    return () => window.clearTimeout(t);
  }, [fromSO, additionalLinesStep, answers, data.questions]);

  // Scoreboard credit for a completed configuration (+100, see api/points).
  // Deliberately fire-and-forget and fully swallowed: points are a nicety on a
  // production quoting tool, so a scoreboard failure must never delay the return
  // to the sales order or surface an error over a submit that actually succeeded.
  // The server dedupes per (user, item), so a re-submit of the same item scores
  // once — this can be called on every successful path without inflating anyone.
  //
  // POINTS ONLY FOR A GENUINELY NEW CONFIG (Kevin, 2026-08-12). Two paths earn nothing,
  // for the same reason: no part was created, so there is nothing to credit.
  //   EDITING     updates a part that already exists; whoever built it was credited then.
  //   EXISTING    the configuration matched a NetSuite item, so Save & Submit builds
  //               nothing and simply brings that item onto the order. That path no longer
  //               calls this helper at all - see the existing-item branch.
  // Which leaves the build path, which by construction has just created a part. The
  // ProgressBar predicts the same rule from configStatus, so what it promises and what
  // this awards cannot disagree.
  //
  // The server's (user, item) dedup does NOT already cover this. It stops the SAME user
  // being paid twice for one item, but Edit Config updates the launched item IN PLACE, so
  // a DIFFERENT user editing someone else's configuration is a new (user, item) pair and
  // would have been credited in full for work they did not do.
  //
  // Gated inside this helper rather than at each call site, so a future success path
  // cannot reintroduce it by calling the obvious function. `isEdit` is the right signal
  // and not merely "launched from Edit Config": it comes from configMode, which
  // forceNewConfig() flips back to 'new' when the item is locked or the user loads a
  // different configuration — both of those DO create a new part, and both should score.
  // `edited` comes from the SERVER's answer when the caller has one. isEdit alone is the
  // pre-request mode, and the launched-item backstop can turn a submit this component thinks
  // is a create into an edit -- which awarded points for a part nobody created, the exact
  // thing the comment above says must not happen.
  const scoreConfiguration = (itemId: string | number | null, itemName: string | null, edited?: boolean) => {
    if (edited ?? isEdit) {
      console.log("[cpq2] no points — editing an existing configuration, not creating one");
      return;
    }
    const ctx = getLaunchContext();
    void awardConfigurationPoints({
      userId: ctx.userId,
      userName: ctx.userName,
      itemId: itemId != null ? String(itemId) : null,
      itemName,
    }).then((r) => {
      if (r) {
        console.log(
          r.awarded
            ? `[cpq2] +${r.points} points (total ${r.total})`
            : `[cpq2] no points — already earned for this item (total ${r.total})`
        );
      }
    });
  };

  // WHO MADE IT AND WHAT AN EDIT CHANGED (util/configChange). A create is recorded so a later
  // edit knows the creator; an edit carries the field-by-field diff from the baseline -- the
  // item's stored snapshot when this session loaded it -- and the server emails the creator
  // when the editor is someone else. Only when the baseline IS this item: diffing against a
  // different part's configuration would report changes nobody made.
  //
  // The baseline then moves to what was just saved, so a second submit in the same session
  // reports only what changed since the first.
  const recordSave = (
    itemId: string | number,
    itemName: string | null,
    edited: boolean,
    savedAnswers: typeof answers
  ) => {
    const id = String(itemId);
    const base = getEditBaseline();
    const known = !!base && base.itemId === id;
    reportConfigChange(
      edited
        ? {
            action: "edit",
            itemId: id,
            itemName,
            previousItemName: known ? base.itemName : null,
            product: data.slug,
            changes: known ? diffConfigs(data, base.answers, savedAnswers) : null,
          }
        : { action: "create", itemId: id, itemName, product: data.slug }
    );
    setEditBaseline({ itemId: id, itemName, answers: savedAnswers });
  };

  // THE OTHER SIDE (Kevin, 2026-10-17: "if I make a left side then what dictates there is a
  // right side"). Nothing did: the rep had to remember a second Configure. An MB housing with
  // a hand is one side of a set, so once its line is on the order the rep is offered the
  // opposite side, opened as its mirror -- reviewed and submitted like any other, never created
  // unseen. The server decides (api/shared/predict/siblings.js mirrorOfSaved: a hand, and its
  // opposite not already saved). Not after an Edit Config, nor for a housing that was itself
  // opened as a mirror: that set is done. Returns true when the offer is showing.
  const offerOppositeSide = async (itemId: string | number, itemName: string): Promise<boolean> => {
    if (isEdit || prediction?.mirrorRole) return false;
    if (answers["BURNER_QTY/MAIN_BURNER_2"] !== true && answers["BURNER_QTY/MAIN_BURNER_3"] !== true) return false;
    try {
      const other = await fetchOppositeSide(data.slug, customerId, {
        transactionId: getLaunchContext().transactionId,
        itemId: String(itemId),
        name: itemName,
        configSnapshot: await getConfigSnapshot(),
      });
      if (!other) return false;
      setOpposite(other);
      setPhase("done");
      return true;
    } catch {
      return false;
    }
  };
  const configureOppositeSide = () => {
    if (!opposite) return;
    // The same URL relaunches the same New Config; PredictLaunch picks this up. Without
    // storage, the reload still launches, and its own MB mirror usually finds this housing.
    stashOppositeSide(data.slug, customerId, opposite);
    window.location.reload();
  };

  const returnToSalesOrder = () => {
    // Guard: window.close can be missing/overridden (e.g. blocked by the browser
    // or reassigned) — never let the return-to-order step throw an uncaught error
    // after the line has already been posted back.
    try {
      if (typeof window.close === "function") window.close();
    } catch {
      /* best effort — the line is already posted to the sales order */
    }
  };
  const dismiss = () => {
    setPhase("idle");
    setMessage("");
    setReturning(false);
    setBypassArmed(false);
    setHeightBlock("");
    setSizeBlock("");
    setBlockedFields([]);
    setExistingFlow(false);
    setBuildStarted(false);
  };

  const submit = async () => {
    // HARD gate (NOT bypassable): an invalid Housing Height must be fixed first.
    // An unmapped value would silently encode to a blank height on the item, so
    // unlike the unanswered-fields warning below there is no "submit anyway".
    // Checked before that warning so it takes priority.
    const badHeight = findInvalidHousingHeight(answers, housingHeightKeys(mappingSpec));
    if (badHeight) {
      noteBlockedSubmit("housing-height", ["BURNER_TYPE"]);
      setHeightBlock(badHeight.value);
      setInvalidFields(["BURNER_TYPE"]);
      setMissing([]);
      setBypassArmed(false);
      const bt = data.questions.find(
        (q) => q.custrecord_cpqc_question_code === "BURNER_TYPE"
      );
      if (bt) onFocusInvalid?.(bt.custrecord_cpqc_question_tab, "BURNER_TYPE");
      return;
    }
    setHeightBlock("");

    // HARD gate (NOT bypassable): a size over its ceiling (see util/sizeLimits). The part
    // number cannot hold it, so there is no "submit anyway".
    const oversize = findOverLimitSizes(data.slug, answers)[0];
    if (oversize) {
      const q = data.questions.find((x) => x.custrecord_cpqc_question_code === oversize.code);
      const label = q ? stripHtml(q.custrecord_cpqc_question_label_html) || q.name : oversize.code;
      noteBlockedSubmit("size-limit", [oversize.code]);
      setSizeBlock(`${label} “${oversize.value}” is over the ${oversize.max}" maximum`);
      setInvalidFields([oversize.code]);
      setMissing([]);
      setBypassArmed(false);
      if (q) onFocusInvalid?.(q.custrecord_cpqc_question_tab, oversize.code);
      return;
    }
    setSizeBlock("");

    // HARD gate (NOT bypassable): fields whose blank value would change the
    // generated PART NUMBER rather than just leave a gap — today Firetube Size
    // (see HARD_REQUIRED_QUESTION_CODES). Checked before the bypassable warning
    // below so "Save and Submit again" can never wave one of these through.
    const blocked = findBlockingUnanswered(data, answers);
    if (blocked.length > 0) {
      noteBlockedSubmit("required-for-part-number", blocked.map((b) => b.code));
      setBlockedFields(blocked.map((b) => `${b.tab} › ${b.label}`));
      setInvalidFields(blocked.map((b) => b.code));
      setMissing([]);
      setBypassArmed(false);
      onFocusInvalid?.(blocked[0].tabId, blocked[0].code);
      return;
    }
    setBlockedFields([]);

    // Guard: every visible, user-editable field must be answered first. The
    // FIRST Save click flags the unanswered fields and arms a bypass; clicking
    // Save & Submit AGAIN saves anyway (an explicit override to submit an
    // incomplete configuration).
    // ON A SALES ORDER THERE IS NO OVERRIDE. Kevin, 2026-08-27: "on the sales order we can
    // no longer allow save and submit anyways for missing fields."
    //
    // A quote keeps it, and has to: a quote deliberately shows only Customer Requirements
    // and three Firetube sections, so an incomplete configuration is the NORMAL state there
    // rather than an oversight. (What it does not have to bypass is the hidden sections --
    // collectRequiredVisible applies the same quote filter, so those are not counted as
    // missing in the first place.)
    //
    // A sales order line becomes a part someone builds, so "save it anyway" was a way to
    // ship a configuration with holes in it.
    // Start of the user's wait, for timeToLineMs below.
    const submitStartedAt = performance.now();
    let timeToLineMs: number | null = null;
    const overrideAllowed = isQuoteLaunch();
    // With an MB middle burner's In / Up cells, which its mirror left for the user.
    const unanswered = findUnansweredVisible(data, answers, mirrorRequiredCells);
    if (unanswered.length > 0 && (!overrideAllowed || !bypassArmed)) {
      noteBlockedSubmit("unanswered", unanswered.map((u) => u.code));
      setInvalidFields(unanswered.map((u) => u.code));
      setMissing(unanswered.map((u) => `${u.tab} › ${u.label}`));
      // Only arm where a second click actually does something. Arming it on a sales order
      // would put "click Save and Submit again to save anyway" on screen and then refuse,
      // which is worse than the old behaviour rather than stricter than it.
      setBypassArmed(overrideAllowed);
      // Take the user straight to the first flagged field (switches tab + scrolls
      // + it renders red) so it's obvious what's missing.
      onFocusInvalid?.(unanswered[0].tabId, unanswered[0].code);
      return;
    }
    setInvalidFields([]);
    setMissing([]);
    setBypassArmed(false);
    // Whether the item is FINISHED, written beside its config snapshot as
    // custitem_unfinishedconfiguration (see util/unfinishedConfig). Not a gate: a quote may
    // save unfinished, and api/notices chases it once a sales order carries the item.
    const unfinishedFields = findUnfinishedFields(data, answers, mirrorRequiredCells);
    const unfinished = unfinishedFields.length > 0;
    if (unfinished) {
      console.log(
        `[cpq2] saving as UNFINISHED (${unfinishedFields.length}): ` +
          unfinishedFields.map((f) => `${f.tab} › ${f.label}`).join("; ")
      );
    }
    // The configuring is over: this click goes through (util/sessionTrack).
    noteSubmitted();
    setPhase("submitting");
    setMessage("");
    setReturning(false);
    setExistingFlow(false);
    setBuildStarted(false);

    // WHAT THE ORDER'S LINES SAY NOW, as cpq2 generated them (util/priorLines). Taken before
    // anything below can move the baseline (recordSave does, on the Existing Config path), and
    // started now so it resolves underneath the build. Edit Config only: a new line has nothing
    // a person could have added to.
    const priorP =
      fromSO && isEdit
        ? priorLineDescriptions(getEditBaseline(), editItemId, data.questions, steps).catch(() => null)
        : Promise.resolve(null);

    try {
      // Times the critical path and prints a breakdown to the console on completion.
      // Diagnostic only -- see src/util/timing.ts; it cannot alter control flow.
      //
      // Started HERE, before the routings and the price, which used to sit outside it: the
      // 2026-10-16 timings showed ~2.5s of a 8.9s wait that no recorded step accounted for.
      const timer = createTimer(`Save & Submit (${data.slug})`);
      const routings = await timer.step("load routings", () => loadRoutings());
      const preview = buildAssembly(answers, {
        itcrs: data.itcrs,
        materials: data.materials,
        routings,
        fingerprintKey,
        computeRouting: getProfile(data.slug).computeRouting,
        bomFallbackComponents: getProfile(data.slug).bomFallbackComponents,
        expectedComponentFamilies: getProfile(data.slug).expectedComponentFamilies,
        buildsRouting: getProfile(data.slug).buildsRouting,
      });
      if (!preview.item?.itemid) {
        setMessage("Could not build the item. " + preview.warnings.join("; "));
        setPhase("error");
        return;
      }
      // Warnings are a hard stop so we never create a half-formed assembly.
      if (preview.warnings.length > 0) {
        setMessage("Resolve before submitting:\n• " + preview.warnings.join("\n• "));
        setPhase("error");
        return;
      }

      // Overflow the user-added Aux 4+ inlet columns into the notes field, and
      // write the built Sales Description (the "DESCRIPTION" op ran in the
      // pipeline) onto the item — cpq2 replaces NetSuite CPQ, which used to do
      // this, so nothing else will.
      // Auto-built overflow of the extra inlet Aux (4+) columns → the item field
      // custitem_extrainletconnections. Kept separate from the editable notes
      // field below (which owns custiteminlet_connection_notes).
      const extraInletConnections = buildInletConnectionNotes(answers, extraAuxColumns);
      // The editable "Inlet Connection Notes" question (INLET_CONNECTION_NOTES)
      // owns the item field custiteminlet_connection_notes 1:1, so it maps
      // there-and-back cleanly (written here, read back by useItemLoader).
      const inletConnectionNotes = String(
        answers["INLET_CONNECTION_NOTES/INLET_CONNECTION_NOTES"] ?? ""
      ).trim();
      const salesDesc = String(answers["DESCRIPTION/DESCRIPTION"] ?? "").trim();

      // ── SALES PRICE ────────────────────────────────────────────────────────
      // Look the configured housing up in the NDB price list, keyed on housing
      // type + design + housing size + housing height (see util/ndbPricing.ts).
      // A hit sets BOTH the item's base price and the sales-order line rate. A
      // miss — any of the four keys blank, no row on file, or rows that disagree
      // — sets neither: the item keeps its default base price and the line keeps
      // the $100 placeholder. Best-effort by construction (lookupNdbPrice never
      // throws), so pricing can never block a submit.
      //
      // Only for a configurator that declares listPricing: the price list and its
      // four keys are Natural Draft's. A product without one keeps the placeholder,
      // which is the same outcome as an NDB config the list cannot price.
      // Usually answered already: the Sales Price panel asked the same question while the
      // user was configuring, and lookupNdbPrice keeps definitive answers (see ndbPricing).
      const listPrice = steps.listPricing
        ? await timer.step("look up the list price", () => lookupNdbPrice(answers))
        : ({ matched: false, key: {}, reason: "this configurator has no price list" } as Awaited<
            ReturnType<typeof lookupNdbPrice>
          >);
      const priceForItem = listPrice.matched ? listPrice.price : undefined;
      const priceForLine = listPrice.matched ? listPrice.price : null;
      // What actually lands on the line, for the confirmation message: the list
      // price when we have one, otherwise the placeholder the bridge falls back to.
      const lineAmountShown = (priceForLine ?? CONFIGURED_LINE_AMOUNT).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (!steps.listPricing) {
        console.log(`[cpq2] list pricing not applicable to ${data.slug} — line keeps the placeholder`);
      } else {
        console.log(
          listPrice.matched
            ? `[cpq2] NDB price ${describeNdbPriceKey(listPrice.key)} -> $${listPrice.price}` +
                (listPrice.reference ? ` (${listPrice.reference})` : "")
            : `[cpq2] NDB price not set (${describeNdbPriceKey(listPrice.key)}): ${listPrice.reason}`
        );
      }

      // ── EXISTING CONFIG ────────────────────────────────────────────────────
      // If this exact configuration already exists as a NetSuite item (matched by
      // its fingerprint), build NOTHING — no new item, BOM, or routing. Just bring
      // that item onto the order with its serial tag + additional lines. This is
      // the third status alongside Editing Config / New Config: whenever the
      // config matches an existing item — editable or not — we reuse it instead of
      // creating or editing anything. The lookup is best-effort: on any failure
      // findItemByFingerprint returns null and we fall through to the normal build
      // path, so it can never block a save.
      // The fingerprint answer key differs per configurator (Natural Draft asks
      // HIDDEN_ID_KEY/ID_KEY, Flame Cells asks ID_KEY/ID) and MUST match that
      // product's ITCR itemid template, because create-assembly stores
      // custitem_fingerprint = item.itemid. Reading the wrong key yields an empty
      // fingerprint, skips this lookup, and rebuilds an item that already exists.
      const fingerprint = fingerprintKey ? String(answers[fingerprintKey] ?? "").trim() : "";
      // REUSE THE LOOKUP THE PAGE ALREADY DID.
      //
      // AnswersContext runs findItemByFingerprint on a 600ms debounce whenever the
      // fingerprint changes, to drive the Existing Config badge. Asking again here cost
      // 1649ms of a measured 13.5s wait -- re-answering a question that was answered while
      // the user was still reading the form.
      //
      // Only when the cached answer is about THIS configuration: existingItemFor is set
      // together with the result and only on completion, so a stale match from a previous
      // fingerprint, an in-flight lookup, or one that never ran all fall through to the live
      // call. Getting that wrong in either direction is expensive -- a wrong match reuses
      // somebody else's part, a wrong miss creates a duplicate -- so the equality is the
      // whole safety argument and there is no "close enough".
      const cacheUsable = !!fingerprint && existingItemFor === fingerprint;
      const existingItem = !fingerprint
        ? null
        : cacheUsable
          ? cachedExistingItem
          : await timer.step("find existing item by fingerprint", () => findItemByFingerprint(fingerprint));
      if (existingItem) {
        // Switch the progress modal to "bringing in" wording — this path builds
        // nothing, so it must not say creating/building/rebuilding.
        setExistingFlow(true);
        const label = existingItem.itemid ?? `item ${existingItem.id}`;
        const refreshFields = preview.item?.fields;
        const matchedIsEdited = isEdit && !!editItemId && String(existingItem.id) === String(editItemId);
        // ITEM UPKEEP, STARTED NOW AND NOT AWAITED HERE.
        //
        // Kevin, 2026-10-16: cut the time from open to submit, without giving up reliability.
        // The price fill and the text refresh below were awaited one after the other BEFORE
        // the line was even resolved -- 1.9s and 2.3s at the median, 5.5s and 6.0s at p90 --
        // and the line needs neither: it carries its own description and its own rate.
        //
        // So they run as one chain alongside the line work, and the line goes out as soon as
        // ITS pieces are ready. The tab still waits for this chain before it closes (see
        // below), because these are ordinary fetches that a closing tab would cut off.
        //
        // One chain, not two parallel calls: both PATCH the same item, and two saves of one
        // NetSuite record at once can fail with "record has been changed".
        const itemUpkeep = (async () => {
          // The matched item may predate list pricing (or never have been priced),
          // which would leave its record disagreeing with the line we're about to
          // write. Fill it in — ONLY when it has no real price, never on a locked
          // item, and only the price field (see setItemBasePrice). Best-effort.
          if (priceForItem != null) {
            const pr = await timer.step("set base price on the existing item", () =>
              setItemBasePrice(existingItem.id, priceForItem)
            );
            console.log(
              `[cpq2] existing item ${label} base price: ${
                pr.priced ? `set to $${priceForItem}` : `not changed (${pr.reason ?? "unknown"})`
              }`
            );
          }
          // Refresh the item's DERIVED TEXT for the same reason the price is filled in
          // above: the matched item may predate the rule that produced it.
          //
          // A fingerprint match means the ANSWERS match. It does NOT mean the text derived
          // from them still matches — when a description rule changes, every item created
          // before it keeps the old wording, and this branch (which builds nothing) is
          // exactly what stopped it ever being rewritten. Re-saving the identical config
          // silently changed nothing, which is what "it should re-update on edit config"
          // was reporting. The server patches only the whitelisted text and never a locked
          // item; best-effort, so a failure here cannot block the line.
          // No matched ITCR means no derived fields to send — there is nothing to refresh
          // WITH, and sending an empty body would be a request that can only no-op.
          //
          // When the match is the very item Edit Config opened, its snapshot and finished flag
          // go too. Filling a field the fingerprint does not hash lands here, matched to itself;
          // without this the answers were dropped and the item stayed "unfinished" for good.
          // Never for a DIFFERENT matched item: its snapshot is its own history.
          if (refreshFields) {
            const snapshot = matchedIsEdited ? await getConfigSnapshot() : null;
            const tr = await timer.step("refresh the existing item's description text", () =>
              refreshItemText(
                existingItem.id,
                refreshFields,
                snapshot ? { config: snapshot, unfinished } : undefined
              )
            );
            console.log(
              `[cpq2] existing item ${label} description text: ${
                tr.refreshed ? `refreshed (${(tr.fields ?? []).join(", ")})` : `not changed (${tr.reason ?? "unknown"})`
              }`
            );
            // The snapshot was rewritten, so this WAS an edit of the item -- one whose changes
            // the fingerprint does not see. Its creator hears about it like any other edit.
            if (snapshot && tr.configWritten) {
              recordSave(existingItem.id, existingItem.itemid ?? null, true, answers);
            }
          }
        })().catch((e) => {
          // Both helpers already report failure as a value; this only catches the unforeseen,
          // so it can never become an unhandled rejection or block the line.
          console.warn("[cpq2] existing item upkeep failed", e);
        });
        // Wait for the upkeep before the tab closes, but never forever: a hung request must
        // not strand the user on this dialog. Both calls are best-effort by design.
        const UPKEEP_WAIT_MS = 25000;
        const upkeepSettled = () =>
          Promise.race([itemUpkeep, new Promise<void>((res) => window.setTimeout(res, UPKEEP_WAIT_MS))]);
        // Customer drawing + model. The item Edit Config opened gets cpq2's files
        // rewritten (fields the fingerprint does not hash may have moved); any other
        // matched item only has an EMPTY field filled -- its files are its own history,
        // and a config that matched it by fingerprint could differ in what it draws.
        attachCustomerFiles({
          slug: data.slug,
          answers,
          itemId: existingItem.id,
          itemName: existingItem.itemid,
          customer: customerName,
          mode: matchedIsEdited ? "replace" : "fill",
          actor: { id: getLaunchContext().userId, name: getLaunchContext().userName },
        });
        if (fromSO) {
          // Resolve the serial tag + additional lines exactly as the build path
          // does (best-effort — a failure here never blocks the main line).
          //
          // All three line inputs at once, and alongside the item upkeep above. None of them
          // writes the item: the tag and the add-ons only read part numbers, and the CPN
          // endpoint writes its own customrecord_scm_customerpartnumber rows.
          const serialTagP = steps.serialTag
            ? timer
                .step("resolve the serial tag", () =>
                  buildSerialTagLine(answers, data.questions, customerId, customerName)
                )
                .catch(() => null)
            : Promise.resolve(null);
          const addlLinesP: Promise<Awaited<ReturnType<typeof resolveAdditionalLines>>> =
            steps.additionalLines
              ? timer
                  .step("resolve additional lines", () =>
                    resolveAdditionalLines(buildAdditionalLines(answers, data.questions))
                  )
                  .catch(() => [])
              : Promise.resolve([]);
          // Customer Part Number — set it on the line even though we build nothing.
          // The endpoint is idempotent (reuses the record when this item already
          // holds the number), so running it here never creates a duplicate; a
          // changed/new CPN follows the normal claim-and-rename logic. Best-effort:
          // a failure just leaves the record id null (the text still shows).
          const cpnIdP: Promise<string | null> = partNumber
            ? timer
                .step("customer part number", () =>
                  createCustomerPartNumber({
                    partNumber,
                    itemId: existingItem.id,
                    customerId,
                    subsidiaryId: preview.routing?.subsidiaryId || null,
                  })
                )
                .then((cpnRes) => cpnRes.created?.id ?? null)
                .catch(() => null)
            : Promise.resolve(null);
          const [serialTag, existingAddlLines, existingCpnId] = await Promise.all([
            serialTagP,
            addlLinesP,
            cpnIdP,
          ]);
          const lineSent = postConfiguredLine(
            { itemId: existingItem.id, itemName: existingItem.itemid ?? "" },
            {
              serialTag,
              additionalLines: existingAddlLines,
              orderFields: buildOrderFields(data.slug, answers),
              description: salesDesc || null,
              customerPartNumberId: existingCpnId,
              customerPartNumber: partNumber || null,
              line: isEdit ? getLaunchContext().line : null,
              amount: priceForLine,
              prior: isEdit ? await priorP : null,
            }
          );
          if (lineSent) {
            // The lines are on their way to the order NOW -- this is the user's wait over.
            timeToLineMs = Math.round(performance.now() - submitStartedAt);
            setMessage(
              `This configuration already exists as ${label} — ${
                isEdit ? "updated the" : "added it to the"
              } sales order line ($${lineAmountShown}${listPrice.matched ? "" : " — no list price"}).`
            );
            // NO POINTS on this path (Kevin, 2026-08-12): "you should only get points if
            // it is genuinely a new config". This branch builds NOTHING - the
            // configuration already matched a NetSuite item, so it just brings that item
            // onto the order. Whoever configured it first was credited then.
            const offered = await offerOppositeSide(existingItem.id, existingItem.itemid ?? "");
            if (!offered) setReturning(true);
            // The item upkeep has been running underneath; let it land before this tab goes.
            await upkeepSettled();
            // Record BEFORE the 800 ms hand-back below closes this tab. cpq2 is gated to
            // NetSuite launches, so this -- Existing Config, returning to a sales order --
            // is the ordinary fast path, and it was the one path that never reported: the
            // build path reports and so does the no-sales-order branch, leaving the most
            // common outcome invisible in cpq2_timing. recordLifecycle prefers sendBeacon,
            // which is what survives the tab closing.
            timer.log();
            timer.report({
              lifecycle: "save-submit",
              product: data.slug,
              configMode: isEdit ? "edit" : "new",
              itemId: existingItem.id,
              itemName: existingItem.itemid ?? null,
              context: {
                path: "existing-config", built: false, fromSalesOrder: true, lineSent: true,
                // Compare THIS across runs: the line now goes out before the item upkeep.
                timeToLineMs,
              },
            });
            if (!offered) window.setTimeout(returnToSalesOrder, 800);
          } else {
            setMessage(
              `⚠ Could not reach the sales order window. This configuration already exists as ${label} (id ${existingItem.id}).`
            );
            setPhase("error");
          }
        } else {
          // Nothing to post, so "done" must mean the item upkeep is done too.
          await upkeepSettled();
          setMessage(
            `This configuration already exists as ${label} (id ${existingItem.id}). Not opened from a sales order, so nothing was added.`
          );
          // Print the breakdown before the modal flips to done, so a fast local
          // step that follows cannot be mistaken for part of the wait.
          timer.log();
          // And persist it. This branch builds nothing, so it is the FAST path — worth
          // recording precisely because it is the baseline the slow ones are judged against.
          timer.report({
            lifecycle: "save-submit",
            product: data.slug,
            configMode: isEdit ? "edit" : "new",
            itemId: existingItem.id,
            itemName: existingItem.itemid ?? null,
            context: { path: "existing-config", built: false, fromSalesOrder: fromSO },
          });
          setPhase("done");
        }
        return;
      }

      // Past the existing-item check — we're going to build. Now (and only now) the
      // modal may show create/build wording.
      setBuildStarted(true);

      // These five item fields are written DIRECTLY (they are not in the encoded
      // mapping), and every one of their value maps is keyed by Natural Draft ANSWER
      // CODES — so they only apply to a configurator that declares ndbItemFields.
      // Core Type shows why a shared code is not enough: NDB answers NA /
      // STD_LARGE_CORE_PIPE / TUBE / PIPE / ROUND_ROD_MACHINED, Flame Cells answers
      // CORE_LARGE_LOW_NOISE / CORE_PIPE / CORE_ROUND_ROD_MACHINED / …, so every
      // lookup would miss and write undefined. Flame Cells needs its own
      // answer-code-to-list-id map before custitemflamecell_coretype can be set from
      // there.
      // Core Type → custitemflamecell_coretype (set directly; not in the mapping).
      const coreTypeCode = steps.ndbItemFields
        ? Object.keys(CORE_TYPE_IDS).find((code) => answers[`CORE_TYPE/${code}`] === true)
        : undefined;
      // Hood Sight Tube → custitem_hoodsighttube (None=1, 1-5/8"=2).
      const hoodSightTubeCode = steps.ndbItemFields
        ? Object.keys(HOOD_SIGHT_TUBE_IDS).find((code) => answers[`HOOD_SIGHT_TUBE/${code}`] === true)
        : undefined;
      // Burner Below Centerline → custitem_burnerbelowcenter (0"=1 … 2"=4).
      const burnerBelowCenterCode = steps.ndbItemFields
        ? Object.keys(BURNER_BELOW_CENTERLINE_IDS).find(
            (code) => answers[`BURNER_BELOW_CENTERLINE/${code}`] === true
          )
        : undefined;
      const itemFields: Record<string, string> = { ...preview.item.fields };
      if (steps.ndbItemFields && inletConnectionNotes)
        itemFields.custiteminlet_connection_notes = inletConnectionNotes;
      if (steps.ndbItemFields && extraInletConnections)
        itemFields.custitem_extrainletconnections = extraInletConnections;
      // (no salesdescription here — the ITCR mapping owns `description`; see above)
      if (coreTypeCode) itemFields.custitemflamecell_coretype = CORE_TYPE_IDS[coreTypeCode];
      if (hoodSightTubeCode) itemFields.custitem_hoodsighttube = HOOD_SIGHT_TUBE_IDS[hoodSightTubeCode];
      if (burnerBelowCenterCode) itemFields.custitem_burnerbelowcenter = BURNER_BELOW_CENTERLINE_IDS[burnerBelowCenterCode];

      // PHASE 1 — the only part the user waits on: create just the item.
      // Creating a serialized assembly item runs its afterSubmit scripts
      // synchronously in NetSuite (incl. the mapping decoder), so the POST can
      // take longer than the request timeout even though NetSuite goes on to
      // finish the create. A bare timeout here would abort the whole submit and
      // orphan that item (no BOM/routing, never added to the order — exactly the
      // failure we saw). create-assembly is idempotent (dedups by
      // custitem_fingerprint), so on a timeout we retry: the retry finds the item
      // the timed-out call created and returns it, letting the flow continue to
      // BOM/routing + the sales-order write-back.
      const configSnapshot = await timer.step("build the config snapshot (local, deflate)", () =>
        getConfigSnapshot()
      );
      // START THE LINE LOOKUPS NOW, ALONGSIDE THE ITEM CREATE.
      //
      // Kevin, 2026-08-27: "my concurency is 7." Measured the same day and he is right --
      // 5 SuiteQL in 571ms against 1730ms serialised, and 6 record GETs in 1228ms against
      // 7559ms. This file's own comment below still says REST serves one request at a time;
      // that was true when it was written (2026-08-05, 6 parallel GETs took 61s) and is not
      // true now. Everything in this session that was designed around "fewer calls, never
      // concurrent calls" was working from the stale number.
      //
      // The Serial Tag and the additional lines resolve part numbers to internal ids from
      // the ANSWERS and the CUSTOMER. Neither needs the item, so neither has any reason to
      // wait for a ~6s POST -- they were simply next in a chain. Started here, they run
      // underneath it and are awaited below, where they are actually used.
      //
      // .catch on each promise at the point of creation, not at the await: an unattached
      // rejection between here and there would surface as an unhandled rejection, and these
      // are best-effort by design -- a missing tag must never cost the user their line.
      const serialTagEarly: Promise<Awaited<ReturnType<typeof buildSerialTagLine>> | null> =
        fromSO && steps.serialTag
          ? buildSerialTagLine(answers, data.questions, customerId, customerName).catch(() => null)
          : Promise.resolve(null);
      const additionalLinesEarly: Promise<Awaited<ReturnType<typeof resolveAdditionalLines>>> =
        fromSO && steps.additionalLines
          ? resolveAdditionalLines(buildAdditionalLines(answers, data.questions)).catch(() => [])
          : Promise.resolve([]);

      const CREATE_ATTEMPTS = 2;

      // A timed-out create is NOT a failed create, and treating it as one is what
      // put "NetSuite took too long to confirm the item" in front of users.
      //
      // Creating the item runs its afterSubmit scripts SYNCHRONOUSLY -- five user events
      // are deployed on serializedAssemblyItem -- and Azure Static Web Apps gives a managed
      // function ~45s. So a slow account, or enough competing traffic, can push this POST
      // past the gateway budget while NetSuite carries on and finishes the create perfectly
      // well. The gateway stops listening; NetSuite does not stop working.
      //
      // This used to say REST serves ONE request at a time and a second caller queues for
      // ~60s. That was measured on 2026-08-05 and is NO LONGER TRUE: re-measured
      // 2026-08-27 at Kevin's prompting ("my concurency is 7"), 6 parallel record GETs
      // took 1228ms against 7559ms serialised. The recovery below is still right -- a
      // timeout is not a failed create either way -- but do not reason from the old number.
      //
      // Reaching PHASE 1 at all means the EXISTING CONFIG lookup above resolved
      // this fingerprint to NOTHING. So if it resolves NOW, the item appeared while
      // we were waiting -- our own timed-out call is what made it. That is what
      // makes adopting it a deduction rather than a guess, and it is the same
      // identity create-assembly's own dedup already runs on.
      //
      // Probing also paces the retry. The old code retried IMMEDIATELY, which is the worst
      // possible move when the first attempt is still running: the retry competes with the
      // very request it is trying to replace, and may well create a second item.
      const RECOVER_PROBES = 3;
      const RECOVER_DELAY_MS = 5000;
      const sleep = (ms: number) => new Promise<void>((res) => window.setTimeout(res, ms));
      const recoverTimedOutCreate = async (): Promise<typeof r> => {
        if (!fingerprint) return null; // no key to confirm with; fall back to retrying
        for (let probe = 1; probe <= RECOVER_PROBES; probe++) {
          await sleep(RECOVER_DELAY_MS);
          // Best-effort by contract: resolves null on any proxy failure, so a
          // failed probe can never turn a good submit into an error.
          const found = await findItemByFingerprint(fingerprint);
          if (found) return { ok: true, item: { id: found.id, itemid: found.itemid ?? undefined } };
        }
        return null;
      };
      let r: Awaited<ReturnType<typeof createAssembly>> | null = null;
      // Set only by the timeout recovery below, when neither response that could have priced
      // the item arrived. It is the ONE case where cpq2 sends its own price to phase 2: an
      // answer merely lacking a `price` field is not evidence -- a save refused by the item's
      // lock answers that way too, and phase 2 does not check the lock.
      let recoveredUnpriced = false;
      // Captured here rather than read inside the closure below: TypeScript cannot keep
      // the earlier non-null narrowing of preview.item across a callback boundary, and
      // a non-null assertion would throw that check away instead of preserving it.
      const plannedItemid = preview.item.itemid;
      // The create request, built in one place: the timeout recovery below sends the very
      // same request again, and two copies of it would drift.
      const createPayload = (): Parameters<typeof createAssembly>[0] => ({
              item: { itemid: plannedItemid, fields: itemFields },
              config: configSnapshot,
              unfinished,
              itemOnly: true,
              // The part number resolved while the user was still configuring. Sent only
              // when it belongs to THIS configuration; the server re-verifies it anyway and
              // resolves from scratch if anything has moved, so it can save four SuiteQL
              // round trips but can never produce a wrong name.
              plannedName: cacheUsable ? plannedName : null,
              // Admin override for custitem_cannoteditcpq, when one is active. Sent on the
              // request that WRITES and re-validated there -- the form being unlocked is
              // not authorisation, and every use is recorded against the operator.
              ...(adminOverride ? { adminOverride } : {}),
              // The POST carries the name, the description and the mandatory fields; the
              // mapping follows in the same background call that builds the BOM. One
              // background request, not two: NetSuite REST is concurrency 1, so a second
              // would only queue behind the first.
              deferFields: true,
              editItemId: editItemId || undefined,
              // The item Edit Config was opened on, sent on EVERY submit and never cleared --
              // unlike editItemId, which the UI drops whenever it switches to New Config.
              //
              // Kevin, 2026-08-27: "if i have BT-MB54/54-30 ... and change a bunch of things
              // but the base is still BT-MB54/54-30 ... then it will never go to a -2."
              // The server enforces that with this: a launched, unlocked, same-base item is
              // edited rather than duplicated, whatever mode this component thinks it is in.
              // That way the rule survives the UI losing edit mode, which is what kept
              // turning one configuration into -1, -2 and -3.
              launchedItemId: getLaunchContext().itemId || undefined,
              basePrice: priceForItem,
              // See DEFER_ITEM_PRICE.
              deferPrice: DEFER_ITEM_PRICE,
              // Audit context for custitem_pricechangenotes. The operator comes
              // from the launch URL because every cpq2 write authenticates as the
              // same integration user — NetSuite's own trail cannot tell one
              // salesperson from another. The reference records which price-list
              // key produced the number, so a disputed price is traceable.
              actor: { name: getLaunchContext().userName, id: getLaunchContext().userId },
              priceReference: listPrice.matched
                ? `NDB ${describeNdbPriceKey(listPrice.key)}${
                    listPrice.reference ? " " + listPrice.reference : ""
                  }`
                : null,
      });
      for (let attempt = 1; attempt <= CREATE_ATTEMPTS; attempt++) {
        try {
          r = await timer.step(`create the item (attempt ${attempt})`, () =>
            createAssembly(createPayload(), { dryRun: false })
          );
          break;
        } catch (e) {
          // Retry only slow/ambiguous failures — a real rejection (bad field,
          // HTTP 400) would just fail again. create-assembly is idempotent
          // (dedups by custitem_fingerprint), so a retry after a server timeout,
          // an opaque SWA gateway 502/504, or a dropped connection finds the item
          // the first call may have created and returns it — never a duplicate.
          //
          // The rule itself lives in netsuiteClient, beside the code that sets the
          // flags it reads, and is pinned by test:recovery. It now also covers an
          // OPAQUE 5xx --
          // a response with NO explanation in it, which cannot have come from
          // create-assembly, because every failure that endpoint can see it answers as
          // JSON naming the cause. The Azure host answered instead, having killed the
          // function: a create makes ~19 sequential NetSuite calls and NetSuite REST
          // runs ONE request at a time, so a second submit alongside this one queues it
          // (~61s) past the ~45s SWA limit -- and the item may well have been created
          // before the host gave up. Only 502/504 were listed here, so that case
          // reached Kevin as a bare "Create failed (HTTP 500)" with no probe and no
          // item on the order. Reported 2026-08-19 on a Natural Draft save.
          const retryable = isAmbiguousCreateFailure(e);
          // Record the classification INPUTS as well as the attempt, because when a retry
          // decision turns out to have been wrong, "what did it know" is the whole question.
          // The fingerprint and the snapshot are what make the configuration reloadable.
          errorContextRef.current = {
            ...errorContextRef.current,
            ...describeThrown(e),
            phase: `create the item (attempt ${attempt})`,
            fingerprint: fingerprint || null,
            itemName: plannedItemid,
            configSnapshot: configSnapshot ?? null,
            context: { ...(errorContextRef.current.context ?? {}), attempt, retryable, isEdit },
          };
          if (!retryable) throw e;
          // Ask NetSuite whether the item landed before deciding this failed.
          setMessage("NetSuite is slow to answer — checking whether the item was created…");
          const recovered = await recoverTimedOutCreate();
          if (recovered) {
            // It did land. Carry on to BOM/routing and the order line exactly as a
            // clean create would, so the timeout costs the user nothing.
            r = recovered;
            // Until the re-read below succeeds, nothing has confirmed this item was priced.
            recoveredUnpriced = true;
            // ...EXCEPT FOR WHAT ONLY THE LOST RESPONSE CARRIED. With deferFields the create
            // POST leaves the custitem_* mapping off and hands it back in the response for
            // finish-assembly to PATCH -- and the price likewise, with deferPrice. A timed-out
            // create never delivered that response, so a recovered item used to reach
            // finish-assembly with no fields at all and keep an empty mapping for good: a
            // re-save matches it by fingerprint and only refreshes its text.
            //
            // Asking once more is safe because it is the same request: create-assembly dedups
            // on the fingerprint, finds this item, and returns its deferred fields. Only those
            // are taken -- the item, and whether this was a create, stay as recovered, so a
            // timeout cannot turn a new part into an "edit". Best-effort: if this fails too,
            // the line still goes out exactly as it did before.
            try {
              const again = await timer.step("re-read the recovered item's deferred fields", () =>
                createAssembly(createPayload(), { dryRun: false })
              );
              if (again?.item?.id != null && String(again.item.id) === String(recovered.item?.id)) {
                r = {
                  ...recovered,
                  deferredFields: again.deferredFields,
                  deferredFieldCount: again.deferredFieldCount,
                  pendingPrice: again.pendingPrice ?? null,
                  price: again.price ?? null,
                  recordType: again.recordType ?? null,
                };
                // The re-read priced it (phase 1) or handed the price back (pendingPrice).
                recoveredUnpriced = false;
              }
            } catch {
              /* keep the recovered item as it is */
            }
            break;
          }
          if (attempt < CREATE_ATTEMPTS) {
            setMessage("NetSuite is taking longer than usual — retrying…");
            continue;
          }
          // Exhausted retries AND the fingerprint still resolves to nothing, so the
          // item genuinely does not exist -- this is now a clean "nothing happened",
          // not the ambiguity the old wording had to hedge about.
          setMessage(
            "NetSuite took too long to respond, and no item was created — nothing " +
              "was added to the order.\n\nThis usually means NetSuite was busy with " +
              "another request. Wait a moment and re-run Save and Submit — it won't " +
              "create a duplicate."
          );
          setPhase("error");
          return;
        }
      }
      if (!r || !r.item?.id) {
        setMessage("NetSuite returned no item id — nothing was added to the order.");
        setPhase("error");
        return;
      }
      const itemId = r.item.id;

      // ADOPT WHAT WE JUST BUILT, so a SECOND Save & Submit in this session edits that part
      // instead of making another one.
      //
      // Kevin, 2026-08-27: "I have had users make changes and it keep changing to the next
      // number so -2." This is how. A create leaves editItemId null, so the session is still
      // in New Config; the user tweaks something, submits again, and create-assembly takes
      // the create path and allocates the next index. The evidence is unambiguous -- pairs
      // like HCR-SB16/12-6-1 and -2, or BT-SB40/40-26LN-1 and -2, are separate records a
      // minute apart, both unlocked, with different fingerprints. Two configurations, two
      // parts, when the second was meant to correct the first.
      //
      // NOT adopted when the item is locked to CPQ edits: that is the one case where a new
      // part IS right, and it is the rule Kevin states -- "it should only be a -2 if it's
      // genuinely a new config meaning the item is cpq cannot edit is true". The server also
      // refuses to patch such an item, so adopting one would just produce a refusal later.
      if (!r.item.cannotEdit) editExistingItem(String(itemId));

      // THE SERVER DECIDES WHETHER THIS WAS AN EDIT, NOT `isEdit`.
      //
      // `isEdit` is computed from configMode at render, BEFORE the request. Since the server
      // gained the launched-item backstop it can turn a submit this component believes is a
      // create into an edit of the launched part, and every consumer below was still reading
      // the stale answer. Review, 2026-08-27, found three consequences and all three are
      // real: the operator was awarded points for a part nobody created, the modal said
      // "added to the sales order line" when the item was updated in place, and the line was
      // posted back as `line: null`, which puts buttons.js in 'skip' mode so the launched
      // config's own Serial Tag and additional lines keep the PREVIOUS description and price.
      // ...AND A REFUSAL IS NOT AN EDIT EITHER. `|| isEdit` below used to make a locked
      // item's untouched hand-back look like a successful update: see src/util/saveOutcome.ts.
      const outcome = describeSaveOutcome(r, isEdit);
      const serverEdited = outcome.edited;

      // A part the configuration needs but has no item mapped for: the BOM was created
      // SHORT of it and its name carries the leading "!" to say so, and the four people who
      // maintain the mapping are told once per configuration. Fired here, after the item
      // exists, and deliberately not awaited -- a mail failure must not turn a submit that
      // worked into an error the user sees. It is NOT a blocking warning for the same
      // reason: refusing to build the cell at all would be the worse outcome.
      if (preview.missingComponents.length > 0) {
        reportMissingParts({
          fingerprint,
          missing: preview.missingComponents,
          itemid: preview.item.itemid,
          bomName: preview.item.bomName,
          product: data.slug,
          detail:
            `Item id ${itemId}. The configuration needs ` +
            `${preview.missingComponents.join(", ")} but no item is mapped for it.`,
        });
      }

      // PHASE 2 — BOM + routing. ESSENTIAL, so we AWAIT them rather than
      // fire-and-forget (a background keepalive was racing the tab close and
      // getting cut off). We call finish-assembly UNCONDITIONALLY — including for
      // Edit Config — because it is idempotent by stable internal ids, checked
      // independently for the BOM and the routing: it backfills whichever piece
      // is missing (a never-built BOM, or a routing that was deleted while the
      // BOM remains) and skips whichever already exists, so it never duplicates.
      // (An earlier "skip when editItemId" guard left stranded items with no
      // BOM/routing, and a coarse "skip if the item has any BOM" check refused to
      // rebuild a deleted routing.)
      try {
        // On Edit Config the item was renamed in place (e.g. ot-fah16/16-22-1 →
        // ht-fah16/16-22-1), and the BOM/routing record NAMES embed that config
        // key, so they go stale. `rebuild: isEdit` tells finish-assembly to refresh
        // the BOM name + routing name to match the renamed item. The BOM component
        // (a fixed placeholder) and the 5 generic routing steps are config-
        // INDEPENDENT, so this is a plain field PATCH — no sublist replace. New
        // Config (rebuild:false) just backfills a missing BOM/routing.
        // A routing with no steps is not a routing: Flange has none and Pilot's ITCR
        // sets routing:false, so both legitimately produce zero steps. Passing the
        // object anyway would have filed an EMPTY manufacturing routing against their
        // BOM (the server guards this too - see hasRoutingSteps).
        // ── TWO-LEVEL BOM: BUILD THE CHILD FIRST ───────────────────────────
        //
        // Kevin, 2026-09-08: "a flame cell with a face plate will always have a flame cell
        // with no face plate in its bom ... you will have to build both items and both boms."
        // buildAssembly put a placeholder in this BOM (see ProductProfile.nestedChildWhen)
        // and it has to become the bare cell's internal id before the revision is written.
        //
        // AWAITED, unlike everything else in phase 2, and the ordering is not merely tidy.
        // BOM idempotency is "create only when the item has no BOM attached", so a parent
        // whose BOM is written while the child is still unresolved keeps a short component
        // list FOREVER -- every later run sees a BOM already there and skips it, reporting
        // success. Resolving the child first is what makes the component list complete the
        // first time it is written.
        //
        // The child's OWN BOM and routing follow in the background, like any other item's:
        // the parent only needs its id. If that background build fails, the child is an item
        // with no BOM, which a re-run repairs -- the same recovery every item already has.
        let bomToBuild = preview.bom;
        if (preview.needsNestedChild) {
          const child = deriveNestedChild({
            answers, tables, selectedRows, data, profile: getProfile(data.slug), routings,
          });
          if (!child) {
            setMessage(
              `Item ${r.item?.itemid ?? preview.item.itemid} (id ${itemId}) was saved, but the ` +
                `cell that goes inside it could not be worked out, so its BOM was not built.\n\n` +
                `Re-run Save and Submit — it won't create a duplicate.`
            );
            setPhase("error");
            return;
          }
          if (child.preview.warnings.length > 0) {
            setMessage(
              `Item ${r.item?.itemid ?? preview.item.itemid} (id ${itemId}) was saved, but the ` +
                `cell that goes inside it (${child.itemid}) cannot be built yet:\n• ` +
                child.preview.warnings.join("\n• ")
            );
            setPhase("error");
            return;
          }
          const childRes = await createAssembly({
            item: { itemid: child.itemid, fields: child.preview.item?.fields ?? {} },
            itemOnly: true,
            deferFields: true,
            actor: { name: getLaunchContext().userName, id: getLaunchContext().userId },
          });
          const childId = childRes?.item?.id;
          if (!childId) {
            setMessage(
              `Item ${r.item?.itemid ?? preview.item.itemid} (id ${itemId}) was saved, but the ` +
                `cell that goes inside it (${child.itemid}) could not be created, so its BOM ` +
                `was not built.\n\nRe-run Save and Submit — it won't create a duplicate.`
            );
            setPhase("error");
            return;
          }
          bomToBuild = substituteNestedChild(preview, childId).bom;
          // The child's own material BOM and routing, in the background.
          finishAssemblyInBackground({
            itemId: String(childId),
            bom: child.preview.bom,
            routing: child.preview.routing.operations.length > 0 ? child.preview.routing : null,
            fields: childRes.deferredFields,
            recordType: childRes.recordType ?? null,
            actor: { name: getLaunchContext().userName, id: getLaunchContext().userId },
          });
        }
        const routingToBuild = preview.routing.operations.length > 0 ? preview.routing : null;
        // NOT AWAITED. Kevin, 2026-08-26: "the user only has to wait for the necessities
        // and then the other parts will work in the background."
        //
        // The lifecycle timings say this step cost 4387ms of an 11.4s median Save and
        // Submit -- the second largest piece -- and the sales order line needs none of it.
        // The line references the ITEM, which already exists by the time this fires; a BOM
        // and a routing are manufacturing records that nothing on the order reads.
        //
        // Fired keepalive so it survives returnToSalesOrder() closing this tab. Failures
        // cannot surface here for the same reason, so finish-assembly writes its own
        // cpq2_error_log row naming the item and the step -- that is what keeps this from
        // repeating the silent customer_requirements failure. It is idempotent by stable
        // internal ids, so the recorded fix is simply to re-run Save and Submit.
        //
        // actor rides along so the BOM and revision memos can record WHO built them.
        // NetSuite's own Created By cannot: every cpq2 write authenticates as the same
        // integration user, so it reads the same for every salesperson.
        finishAssemblyInBackground({
          itemId, bom: bomToBuild, routing: routingToBuild, rebuild: serverEdited,
          // The mapping fields the create deliberately left off the POST. Applied FIRST,
          // before the BOM, so the item is only a name-and-description for as short a
          // time as possible. Absent when the server did not defer (or fell back).
          fields: r.deferredFields,
          // The part-number reservation create-assembly planned but did not write, so its
          // ~765ms stayed out of the wait. finish-assembly runs with the item already
          // created, so the "row only after the item" rule still holds.
          configMapRow: r.configMapRow ?? null,
          // The list price the server left for this call (deferPrice). For a create recovered
          // from a timeout whose re-read also failed (recoveredUnpriced), cpq2's own: the write
          // is idempotent, so a duplicate costs nothing, and a missing one would leave the item
          // unpriced. Never otherwise -- see recoveredUnpriced.
          price:
            r.pendingPrice ??
            (priceForItem != null && recoveredUnpriced
              ? {
                  price: priceForItem,
                  source: serverEdited ? "cpq2:edit" : "cpq2:submit",
                  reference: listPrice.matched
                    ? `NDB ${describeNdbPriceKey(listPrice.key)}${listPrice.reference ? " " + listPrice.reference : ""}`
                    : null,
                }
              : null),
          // The subtype create-assembly used. Phase 2 patches this same item, and guessing
          // the endpoint is what left orphan BOMs on legacy cells.
          recordType: r.recordType ?? null,
          actor: { name: getLaunchContext().userName, id: getLaunchContext().userId },
        });
      } catch (e) {
        // Only a synchronous throw while ASSEMBLING the payload can land here now; the
        // request itself no longer reports back. Kept so a bad preview cannot pass silently.
        setMessage(
          `Item ${r.item?.itemid ?? preview.item.itemid} (id ${itemId}) was saved, but the ` +
            `manufacturing records could not be started:\n${e instanceof Error ? e.message : String(e)}\n\n` +
            `Re-run Save and Submit to retry — it won't create a duplicate.`
        );
        setPhase("error");
        return;
      }

      // Customer Part Number — AWAITED (not fire-and-forget) so we get the new
      // record's internal id back and can set it on the configured line's CPN
      // column. custcol_scm_customerpartnumber is a SELECT that references this
      // record; relying on NetSuite to source it raced with the record's
      // creation and left the line column blank. Best-effort: a failure must
      // never block the line, so on error we fall back to no id (the item↔record
      // link still stands from whatever committed). It runs AFTER BOM/routing, so
      // this single sequential call adds no concurrency burst.
      let cpnRecordId: string | null = null;
      // SAY SO WHEN IT DOES NOT LAND. Swallowing this entirely meant a line could go out with
      // the part number as text but no link to the CPN record, and nobody would know until
      // someone looked at the column. The line is still the priority -- this never blocks it.
      let cpnProblem: string | null = null;
      if (partNumber) {
        try {
          // Timed: it sits on the wait after the create and was the largest unmeasured step.
          const cpnRes = await timer.step("customer part number", () =>
            createCustomerPartNumber({
              partNumber,
              itemId,
              customerId,
              subsidiaryId: preview.routing?.subsidiaryId || null,
            })
          );
          cpnRecordId = cpnRes.created?.id ?? null;
        } catch (e) {
          cpnRecordId = null;
          cpnProblem = e instanceof Error ? e.message : String(e);
          console.warn("[cpq2] customer part number not linked to the line: " + cpnProblem);
        }
      }
      // customrecord_customer_requirements is a port of Natural Draft's
      // CREATE_CUSTOMER_REQUIREMENTS script and its whole field set is keyed to
      // Natural Draft questions. This CREATES A REAL RECORD against a live customer,
      // so a configurator that has not declared it must not reach here — otherwise
      // every submit files a near-empty requirements record nobody asked for.
      if (steps.customerRequirements) {
        fireBackground("/customer-requirements", {
          fields: buildCustomerRequirements(answers, data.questions, getLaunchContext()),
          references: CUSTOMER_REQUIREMENTS_REFERENCE_FIELDS,
          dryRun: false,
        });
      } else {
        console.log(`[cpq2] customer requirements not applicable to ${data.slug} — not created`);
      }

      // Emergency-only auto-backup: save the config into the Favorites table
      // (cpq2_saved_configurations) flagged is_auto=true, so it's recoverable but
      // HIDDEN from the normal Favorites Browse list. Named after the item so it's
      // findable (GET /api/favorites?scope=auto&q=<part no>). Best-effort,
      // fire-and-forget — a backup failure never affects the save.
      fireBackground("/favorites", {
        name: r.item?.itemid ?? preview.item.itemid,
        configSnapshot,
        model: String(answers["HOUSING_VALIDATION/HOUSING_VALIDATION"] ?? "").trim() || null,
        customerId,
        customerName,
        customerPartNumber: partNumber || null,
        originType: getLaunchContext().originType,
        originTranid: getLaunchContext().transactionId ?? getLaunchContext().custbody102,
        createdById: getLaunchContext().userId,
        createdByName: getLaunchContext().userName,
        // Which configurator produced it. The Browse lists filter on this column
        // strictly, so a row saved without it would be invisible to every product --
        // and an emergency backup nobody can find is not a backup.
        product: data.slug,
        isAuto: true,
      });

      // The customer's Serial Tag line (added once, qty 1, $0) and the additional line
      // items (Flange Gasket, Ext Nipple, Main Burner, Nozzle), which ride along with the
      // configured line. BOTH were started before the item create and have been resolving
      // underneath it, so these awaits are normally already settled -- same values, same
      // best-effort semantics, just not spent one after the other while the user waits.
      // Timed as one step: normally ~0ms, and if it ever is not, that is the finding.
      const [serialTag, additionalLines] = await timer.step(
        "wait for the serial tag and additional lines",
        () =>
          Promise.all([
            serialTagEarly,
            fromSO ? additionalLinesEarly.catch(() => []) : Promise.resolve([]),
          ]) as Promise<[Awaited<typeof serialTagEarly>, Awaited<ReturnType<typeof resolveAdditionalLines>>]>
      );

      // BOM/routing are built — now push the line to the sales order and return.
      // In Edit Config the launched line is updated in place (line = the launch
      // line) and its description is refreshed; in New Config a new line is added
      // (line = null). salesDesc refreshes the configured line's description.
      const lineSent = fromSO
        ? postConfiguredLine(
            { itemId, itemName: r.item?.itemid ?? preview.item.itemid },
            {
              serialTag,
              additionalLines,
              orderFields: buildOrderFields(data.slug, answers),
              description: salesDesc || null,
              customerPartNumberId: cpnRecordId,
              customerPartNumber: partNumber || null,
              line: serverEdited ? getLaunchContext().line : null,
              amount: priceForLine,
              prior: serverEdited ? await priorP : null,
            }
          )
        : false;

      // THE NUMBER THAT MATTERS. Kevin, 2026-08-27: "the goal is to save and get line items
      // to start populating on the sales order or quote asap."
      //
      // Nothing was measuring that. total_ms covers the whole lifecycle, so it kept moving
      // for reasons the user never waits through, and every comparison drawn from it -- mine
      // included -- was answering a different question. This marks the moment the lines are
      // written, which is the moment the user is done waiting.
      timeToLineMs = Math.round(performance.now() - submitStartedAt);

      // The item exists and its BOM + routing are built — the configuration is
      // done, so score it. Credited whether or not the write-back reached the
      // sales-order window: the work was completed either way, and the two
      // branches below differ only in how the user gets back to the order.
      // REFUSED BY THE LOCK: the server wrote nothing. Report it and stop here, before the
      // points call, the "updated" copy and the return to the sales order -- each of which
      // would otherwise state that something happened. The timing row still goes out, with
      // the reason on it, so a refused save is findable next to the ones that worked.
      if (outcome.refusedByLock) {
        timer.log();
        timer.report({
          lifecycle: "save-submit",
          product: data.slug,
          configMode: isEdit ? "edit" : "new",
          itemId,
          itemName: r.item?.itemid ?? preview.item.itemid ?? null,
          // "error" rather than "ok": the save did not do what was asked. LifecycleOutcome
          // has no "refused", and filing it under ok would hide it among the saves that worked.
          outcome: "error",
          reason: outcome.refusalReason ?? "locked to CPQ edits",
          context: { path: "built", built: false, fromSalesOrder: fromSO, lineSent, refusedByLock: true },
        });
        setMessage(`⚠ ${outcome.refusalReason}`);
        setPhase("done");
        return;
      }

      // Customer drawing + model, in the background. After the lock check above on
      // purpose: a refused save wrote nothing to the item, and neither may this.
      attachCustomerFiles({
        slug: data.slug,
        answers,
        itemId,
        itemName: r.item?.itemid ?? preview.item.itemid,
        customer: customerName,
        mode: "replace",
        actor: { id: getLaunchContext().userId, name: getLaunchContext().userName },
      });

      scoreConfiguration(itemId, r.item?.itemid ?? preview.item.itemid ?? null, serverEdited);
      recordSave(itemId, r.item?.itemid ?? preview.item.itemid ?? null, serverEdited, answers);

      // Breakdown of the whole critical path. Placed BEFORE the branch below so it
      // prints on both outcomes — the sales-order write-back path returns to NetSuite
      // and closes this tab, which would otherwise take the numbers with it.
      timer.log();
      // Persisted for the same reason it is placed here: this tab may be about to close and
      // hand control back to the sales order, taking the numbers with it.
      timer.report({
        lifecycle: "save-submit",
        product: data.slug,
        configMode: serverEdited ? "edit" : "new",
        itemId,
        itemName: r.item?.itemid ?? preview.item.itemid ?? null,
        context: {
          path: "built", built: true, fromSalesOrder: fromSO, lineSent,
          // DID THE FIELD DEFERRAL ACTUALLY HAPPEN? Without this the timing rows cannot
          // answer it, which is exactly where the 8/27 review of this feature stalled: the
          // POST measured 1743ms once and 11.7s twice, and nothing recorded whether the
          // trimmed body was used, whether NetSuite rejected it and the full body was sent
          // instead (deferFellBack), or whether the account was simply slower that hour.
          // A performance change that cannot be told apart from load is not measurable.
          deferredFieldCount: r.deferredFieldCount ?? null,
          deferFellBack: r.deferFellBack === true,
          serverEdited,
          // How long the user actually waited for their lines. Compare THIS across runs,
          // not total_ms.
          timeToLineMs,
          // Whether the badge's lookup was reused instead of repeating it. Without this,
          // a run that happened to skip ~1.6s looks identical to one where NetSuite was fast.
          usedCachedExisting: cacheUsable,
          // Did the pre-resolved name survive the server's re-check? Distinguishes a run
          // that skipped the resolution from one where NetSuite was simply quick.
          usedNameHint: r.usedNameHint === true,
        },
      });

      if (fromSO && lineSent) {
        setMessage(
          `Item ${serverEdited ? "updated on" : "added to"} the sales order line ($${lineAmountShown}${
            listPrice.matched ? "" : " — no list price"
          }).` +
            (cpnProblem
              ? `

⚠ The Customer Part Number "${partNumber}" is on the line as text, but could ` +
                `not be linked to its record: ${cpnProblem}`
              : "")
        );
        if (!(await offerOppositeSide(itemId, r.item?.itemid ?? preview.item.itemid ?? ""))) {
          setReturning(true);
          // Brief beat so the background records dispatch before the tab closes.
          window.setTimeout(returnToSalesOrder, 800);
        }
      } else {
        const head = fromSO
          ? "⚠ Could not reach the sales order window"
          : r.item?.updated
            ? `Updated item ${r.item.itemid ?? ""} (id ${itemId})`
            : `Created item ${preview.item.itemid} (id ${itemId})`;
        setMessage(head);
        setPhase("done");
      }
    } catch (e) {
      // The richest failure there is: the item exists and the BOM/routing did not finish, so
      // the record has to carry enough to say WHY without asking whoever saw it. This is the
      // "BOM revision failed (HTTP 429)" case Luke hit on 2026-08-20.
      errorContextRef.current = {
        ...errorContextRef.current,
        ...describeThrown(e),
        phase: "finish-assembly",
      };
      setMessage(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  };

  return (
    <>
      <div className="cpq-save-submit-wrap">
        <div className="cpq-header-actions">
          {fromSO && (
            <button type="button" className="cpq-cancel-btn" onClick={returnToSalesOrder}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="cpq-save-submit"
            onClick={submit}
            disabled={phase === "submitting"}
          >
            Save and Submit
          </button>
        </div>

        {heightBlock && (
          <div className="cpq-save-submit-error" role="alert">
            <strong>Can’t submit</strong> — the Housing Height “{heightBlock}” isn’t a
            valid size. Pick a listed height (highlighted in red), then Save and Submit.
          </div>
        )}

        {sizeBlock && (
          <div className="cpq-save-submit-error" role="alert">
            <strong>Can’t submit</strong> — {sizeBlock}. Fix it (highlighted in red), then
            Save and Submit.
          </div>
        )}

        {blockedFields.length > 0 && (
          <div className="cpq-save-submit-error" role="alert">
            <strong>Can’t submit</strong> —{" "}
            {blockedFields.length === 1 ? "this field is" : "these fields are"} required
            and can’t be left blank (highlighted in red), because the generated part
            number depends on {blockedFields.length === 1 ? "it" : "them"}:
            <ul>
              {blockedFields.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {missing.length > 0 && (
          <div className="cpq-save-submit-error" role="alert">
            <strong>
              {missing.length} field{missing.length === 1 ? "" : "s"} need
              {missing.length === 1 ? "s" : ""} an answer
            </strong>{" "}
            — highlighted in red:
            <ul>
              {missing.slice(0, 6).map((m) => (
                <li key={m}>{m}</li>
              ))}
              {missing.length > 6 && <li>+{missing.length - 6} more…</li>}
            </ul>
            {bypassArmed ? (
              <div style={{ marginTop: "0.5rem", fontWeight: 600 }}>
                Click <strong>Save and Submit</strong> again to save anyway.
              </div>
            ) : (
              // No override here — say so, rather than leaving the user clicking a button
              // that keeps refusing without explaining why.
              <div style={{ marginTop: "0.5rem", fontWeight: 600 }}>
                These must be answered before this can be submitted.
              </div>
            )}
          </div>
        )}
      </div>

      {phase !== "idle" && (
        <div className="cpq-modal-overlay" role="dialog" aria-modal="true">
          <div className="cpq-modal cpq-modal-status">
            <div className="cpq-modal-body">
              {phase === "submitting" && (
                <>
                  {/* ONE message, never a result stacked under a progress line.
                      Kevin, 2026-08-27: "lets only have one popup when saving."
                      Both hand-back paths -- a fresh build and Existing Config --
                      set the outcome and leave the phase at "submitting", so the
                      dialog used to show "Creating the item in NetSuite…" with the
                      "added to the sales order line" result appended beneath it.
                      `returning` is set on exactly those two paths and nowhere
                      else, so it is the signal that the message IS the outcome.
                      Interim notes (NetSuite slow, retrying) leave it false and
                      still read as a note under the progress line. */}
                  {returning ? (
                    <p className="cpq-modal-ok">✅ {message}</p>
                  ) : (
                    <>
                      <p>
                        {existingFlow
                          ? "This configuration already exists — bringing the item onto the order…"
                          : !buildStarted
                            ? "Submitting your configuration…"
                            : isEdit
                              ? "Updating the item in NetSuite…"
                              : "Creating the item in NetSuite…"}
                      </p>
                      {message && <p className="cpq-modal-note">{message}</p>}
                    </>
                  )}
                  {returning && (
                    <p className="cpq-modal-note">Returning to the sales order…</p>
                  )}
                </>
              )}
              {phase === "done" && (
                <>
                  <p className="cpq-modal-ok">✅ {message}</p>
                  {returning && (
                    <p className="cpq-modal-note">Returning to the sales order…</p>
                  )}
                </>
              )}
              {phase === "error" && (
                <p className="cpq-modal-err" style={{ whiteSpace: "pre-line" }}>
                  ❌ {message}
                </p>
              )}
            </div>

            {phase !== "submitting" && (
              <footer className="cpq-modal-footer">
                {phase === "done" && fromSO && opposite ? (
                  <>
                    <button type="button" className="cpq-btn" onClick={returnToSalesOrder}>
                      Return to Sales Order
                    </button>
                    <button type="button" className="cpq-btn cpq-btn-primary" onClick={configureOppositeSide}>
                      Configure the opposite side
                    </button>
                  </>
                ) : phase === "done" && fromSO ? (
                  <button type="button" className="cpq-btn cpq-btn-primary" onClick={returnToSalesOrder}>
                    Return to Sales Order
                  </button>
                ) : (
                  <button type="button" className="cpq-btn" onClick={dismiss}>
                    Close
                  </button>
                )}
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
}
